import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Badge, Card } from 'react-bootstrap';
import { 
  getGastosFijos, 
  crearGastoFijo, 
  actualizarGastoFijo, 
  eliminarGastoFijo,
  toggleEstadoGastoFijo,
  crearGastoDesdeGastoFijo,
  getCategorias,
  getSubcategorias,
  getTiposGastos
} from '../api/gastos.api.ts';
import Swal from 'sweetalert2';

const GastosFijosModal = ({ show, onHide, onGastoCreado }) => {
  // Estados principales
  const [gastosFijos, setGastosFijos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modoEdicion, setModoEdicion] = useState(false);
  const [gastoFijoSeleccionado, setGastoFijoSeleccionado] = useState(null);
  const [vistaActual, setVistaActual] = useState('lista'); // 'lista' o 'formulario'
  const [filtroGrupo, setFiltroGrupo] = useState('');
  
  // Estados para crear gasto desde plantilla
  const [showModalCrearGasto, setShowModalCrearGasto] = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [formDataGasto, setFormDataGasto] = useState({
    responsable: '',
    fecha_gasto: new Date().toISOString().split('T')[0],
    monto: '',
    observaciones: ''
  });

  // Estados para datos de formulario
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [tiposGastos, setTiposGastos] = useState([]);

  // Estado del formulario
  const [formData, setFormData] = useState({
    gf_nombre: '',
    gf_monto: '',
    gf_tipo_id: '',
    gf_categoria_id: '',
    gf_subcategoria_id: '',
    gf_pais: 'Chile',
    gf_empresa: '',
    gf_metodo_pago: '',
    gf_frecuencia: 'Mensual',
    gf_dia_vencimiento: '',
    gf_grupo: 'Personal',
    gf_observaciones: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    if (show) {
      cargarGastosFijos();
      cargarDatosIniciales();
    }
  }, [show, filtroGrupo]);

  const cargarDatosIniciales = async () => {
    try {
      const [categoriasResp, tiposResp] = await Promise.all([
        getCategorias(),
        getTiposGastos()
      ]);

      if (categoriasResp.success) {
        setCategorias(categoriasResp.data);
      }

      if (tiposResp.success) {
        setTiposGastos(tiposResp.data);
      }
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
    }
  };

  const cargarGastosFijos = async () => {
    setLoading(true);
    setError('');
    try {
      const filtros = filtroGrupo ? { grupo: filtroGrupo } : {};
      const response = await getGastosFijos(filtros);
      
      if (response.success) {
        setGastosFijos(response.data);
      } else {
        setError('Error al cargar gastos fijos');
      }
    } catch (error) {
      setError('Error al conectar con el servidor');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarSubcategorias = async (categoriaId) => {
    try {
      const response = await getSubcategorias(categoriaId);
      if (response.success) {
        setSubcategorias(response.data);
      }
    } catch (error) {
      console.error('Error cargando subcategorías:', error);
    }
  };

  const handleInputChange = (e) => {
    console.log(e.target.name, e.target.value);
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Cargar subcategorías cuando cambia la categoría
    if (name === 'gf_categoria_id' && value) {
      cargarSubcategorias(value);
      setFormData(prev => ({ ...prev, gf_subcategoria_id: '' }));
    }
  };

  const limpiarFormulario = () => {
    setFormData({
      gf_nombre: '',
      gf_monto: '',
      gf_tipo_id: '',
      gf_categoria_id: '',
      gf_subcategoria_id: '',
      gf_pais: 'Chile',
      gf_empresa: '',
      gf_metodo_pago: '',
      gf_frecuencia: 'Mensual',
      gf_dia_vencimiento: '',
      gf_grupo: 'Personal',
      gf_observaciones: ''
    });
    setSubcategorias([]);
    setModoEdicion(false);
    setGastoFijoSeleccionado(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSend = {
        ...formData,
        gf_monto: parseFloat(formData.gf_monto),
        gf_tipo_id: parseInt(formData.gf_tipo_id),
        gf_categoria_id: parseInt(formData.gf_categoria_id),
        gf_subcategoria_id: parseInt(formData.gf_subcategoria_id),
        gf_dia_vencimiento: formData.gf_dia_vencimiento ? parseInt(formData.gf_dia_vencimiento) : null
      };

      let response;
      if (modoEdicion && gastoFijoSeleccionado) {
        response = await actualizarGastoFijo(gastoFijoSeleccionado.gf_id, dataToSend);
      } else {
        response = await crearGastoFijo(dataToSend);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: modoEdicion ? 'Gasto fijo actualizado' : 'Gasto fijo creado',
          text: response.message,
          timer: 2000
        });
        limpiarFormulario();
        setVistaActual('lista');
        cargarGastosFijos();
      } else {
        setError(response.message || 'Error al guardar el gasto fijo');
      }
    } catch (error) {
      setError('Error al conectar con el servidor');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = async (gastoFijo) => {
    setModoEdicion(true);
    setGastoFijoSeleccionado(gastoFijo);
    
    // Cargar subcategorías de la categoría seleccionada
    await cargarSubcategorias(gastoFijo.categoria_id);
    
    setFormData({
      gf_nombre: gastoFijo.gf_nombre,
      gf_monto: gastoFijo.gf_monto,
      gf_tipo_id: gastoFijo.tipo_id,
      gf_categoria_id: gastoFijo.categoria_id,
      gf_subcategoria_id: gastoFijo.subcategoria_id,
      gf_pais: gastoFijo.gf_pais,
      gf_empresa: gastoFijo.gf_empresa || '',
      gf_metodo_pago: gastoFijo.gf_metodo_pago || '',
      gf_frecuencia: gastoFijo.gf_frecuencia,
      gf_dia_vencimiento: gastoFijo.gf_dia_vencimiento || '',
      gf_grupo: gastoFijo.gf_grupo,
      gf_observaciones: gastoFijo.gf_observaciones || ''
    });
    
    setVistaActual('formulario');
  };

  const handleEliminar = async (gastoFijoId) => {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await eliminarGastoFijo(gastoFijoId);
        if (response.success) {
          Swal.fire('Eliminado', response.message, 'success');
          cargarGastosFijos();
        } else {
          Swal.fire('Error', response.message, 'error');
        }
      } catch (error) {
        Swal.fire('Error', 'Error al eliminar el gasto fijo', 'error');
      }
    }
  };

  const handleToggleEstado = async (gastoFijoId) => {
    try {
      const response = await toggleEstadoGastoFijo(gastoFijoId);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Estado actualizado',
          timer: 1500,
          showConfirmButton: false
        });
        cargarGastosFijos();
      }
    } catch (error) {
      Swal.fire('Error', 'Error al cambiar el estado', 'error');
    }
  };

  const handleCrearGastoDesdeTemplate = (gastoFijo) => {
    setPlantillaSeleccionada(gastoFijo);
    setFormDataGasto({
      responsable: '',
      fecha_gasto: new Date().toISOString().split('T')[0],
      monto: gastoFijo.gf_monto,
      observaciones: ''
    });
    setShowModalCrearGasto(true);
  };

  const handleInputChangeGasto = (e) => {
    const { name, value } = e.target;
    setFormDataGasto(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitCrearGasto = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSend = {
        responsable: formDataGasto.responsable.trim(),
        fecha_gasto: formDataGasto.fecha_gasto,
        monto: formDataGasto.monto ? parseFloat(formDataGasto.monto) : null,
        observaciones: formDataGasto.observaciones || null
      };

      const response = await crearGastoDesdeGastoFijo(plantillaSeleccionada.gf_id, dataToSend);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Gasto creado exitosamente',
          timer: 2000
        });
        
        setShowModalCrearGasto(false);
        setPlantillaSeleccionada(null);
        setFormDataGasto({
          responsable: '',
          fecha_gasto: new Date().toISOString().split('T')[0],
          monto: '',
          observaciones: ''
        });
        
        if (onGastoCreado) {
          onGastoCreado(response.data);
        }
        onHide();
      } else {
        setError(response.message || 'Error al crear el gasto');
      }
    } catch (error) {
      setError('Error al conectar con el servidor');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCerrarModalCrearGasto = () => {
    setShowModalCrearGasto(false);
    setPlantillaSeleccionada(null);
    setFormDataGasto({
      responsable: '',
      fecha_gasto: new Date().toISOString().split('T')[0],
      monto: '',
      observaciones: ''
    });
  };

  const getBadgeColor = (grupo) => {
    switch (grupo) {
      case 'Personal': return 'primary';
      case 'Familiar': return 'success';
      case 'Negocio': return 'warning';
      default: return 'secondary';
    }
  };

  const renderLista = () => (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Form.Select 
          value={filtroGrupo} 
          onChange={(e) => setFiltroGrupo(e.target.value)}
          style={{ width: '200px' }}
        >
          <option value="">Todos los grupos</option>
          <option value="Personal">Personal</option>
          <option value="Familiar">Familiar</option>
          <option value="Negocio">Negocio</option>
        </Form.Select>
        
        <Button 
          variant="primary" 
          onClick={() => {
            limpiarFormulario();
            setVistaActual('formulario');
          }}
        >
          <i className="fas fa-plus me-2"></i>
          Nuevo Gasto Fijo
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Cargando gastos fijos...</p>
        </div>
      ) : gastosFijos.length === 0 ? (
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          No hay gastos fijos registrados
        </Alert>
      ) : (
        <div className="row">
          {gastosFijos.map(gasto => (
            <div key={gasto.gf_id} className="col-md-6 mb-3">
              <Card className={!gasto.gf_activo ? 'opacity-50' : ''}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-1">{gasto.gf_nombre}</h6>
                      <Badge bg={getBadgeColor(gasto.gf_grupo)} className="me-1">
                        {gasto.gf_grupo}
                      </Badge>
                      <Badge bg="info">{gasto.gf_frecuencia}</Badge>
                    </div>
                    <h5 className="text-primary mb-0">
                      ${gasto.gf_monto.toLocaleString()}
                    </h5>
                  </div>
                  
                  <div className="small text-muted mb-2">
                    <div><strong>Tipo:</strong> {gasto.tipo_nombre}</div>
                    <div><strong>Categoría:</strong> {gasto.categoria_nombre}</div>
                    {gasto.gf_empresa && <div><strong>Empresa:</strong> {gasto.gf_empresa}</div>}
                    {gasto.gf_dia_vencimiento && (
                      <div><strong>Vence:</strong> día {gasto.gf_dia_vencimiento}</div>
                    )}
                  </div>

                  <div className="d-flex gap-2 mt-3">
                    <Button 
                      size="sm" 
                      variant="success" 
                      onClick={() => handleCrearGastoDesdeTemplate(gasto)}
                      title="Crear gasto desde esta plantilla"
                      className="d-flex align-items-center gap-1"
                    >
                      <i className="fas fa-plus-circle" style={{ fontSize: '1.1rem' }}></i>
                      <span className="d-none d-md-inline">Crear</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="primary" 
                      onClick={() => handleEditar(gasto)}
                      title="Editar plantilla"
                      className="d-flex align-items-center gap-1"
                    >
                      <i className="fas fa-edit" style={{ fontSize: '1.1rem' }}></i>
                      <span className="d-none d-md-inline">Editar</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant={gasto.gf_activo ? 'warning' : 'secondary'}
                      onClick={() => handleToggleEstado(gasto.gf_id)}
                      title={gasto.gf_activo ? 'Desactivar' : 'Activar'}
                      className="d-flex align-items-center gap-1"
                    >
                      <i className={`fas fa-${gasto.gf_activo ? 'toggle-on' : 'toggle-off'}`} style={{ fontSize: '1.1rem' }}></i>
                      <span className="d-none d-md-inline">{gasto.gf_activo ? 'Activo' : 'Inactivo'}</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="danger" 
                      onClick={() => handleEliminar(gasto.gf_id)}
                      title="Eliminar plantilla"
                      className="d-flex align-items-center gap-1"
                    >
                      <i className="fas fa-trash-alt" style={{ fontSize: '1.1rem' }}></i>
                      <span className="d-none d-md-inline">Eliminar</span>
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderFormulario = () => (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={12} className="mb-3">
          <Form.Group>
            <Form.Label>Nombre personalizado *</Form.Label>
            <Form.Control
              type="text"
              name="gf_nombre"
              value={formData.gf_nombre}
              onChange={handleInputChange}
              placeholder="Ej: Arriendo departamento"
              required
            />
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Valor del gasto *</Form.Label>
            <Form.Control
              type="number"
              name="gf_monto"
              value={formData.gf_monto}
              onChange={handleInputChange}
              placeholder="0"
              required
              step="0.01"
            />
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>País</Form.Label>
            <Form.Control
              type="text"
              name="gf_pais"
              value={formData.gf_pais}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Tipo de Categoría *</Form.Label>
            <Form.Select
              name="gf_tipo_id"
              value={formData.gf_tipo_id}
              onChange={handleInputChange}
              required
            >
              <option value="">Seleccionar tipo</option>
              {tiposGastos.map(tipo => (
                <option key={tipo.tipo_id} value={tipo.tipo_id}>
                  {tipo.tipo_nombre}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Categoría *</Form.Label>
            <Form.Select
              name="gf_categoria_id"
              value={formData.gf_categoria_id}
              onChange={handleInputChange}
              required
            >
              <option value="">Seleccionar categoría</option>
              {categorias.map(cat => (
                <option key={cat.cat_id} value={cat.cat_id}>
                  {cat.cat_nombre}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Subcategoría *</Form.Label>
            <Form.Select
              name="gf_subcategoria_id"
              value={formData.gf_subcategoria_id}
              onChange={handleInputChange}
              required
              disabled={!formData.gf_categoria_id}
            >
              <option value="">Seleccionar subcategoría</option>
              {subcategorias.map(sub => (
                <option key={sub.sub_id} value={sub.sub_id}>
                  {sub.sub_nombre}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Empresa</Form.Label>
            <Form.Control
              type="text"
              name="gf_empresa"
              value={formData.gf_empresa}
              onChange={handleInputChange}
              placeholder="Ej: Enel, Movistar, etc."
            />
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Método de pago</Form.Label>
            <Form.Select
              name="gf_metodo_pago"
              value={formData.gf_metodo_pago}
              onChange={handleInputChange}
            >
              <option value="">Seleccionar método</option>
              <option value="Efectivo">Efectivo</option>
              <option value="Transferencia">Transferencia</option>
              <option value="Tarjeta Debito">Tarjeta Débito</option>
              <option value="Tarjeta Credito">Tarjeta Crédito</option>
              <option value="Cheque">Cheque</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Frecuencia de pago *</Form.Label>
            <Form.Select
              name="gf_frecuencia"
              value={formData.gf_frecuencia}
              onChange={handleInputChange}
              required
            >
              <option value="Diaria">Diaria</option>
              <option value="Semanal">Semanal</option>
              <option value="Quincenal">Quincenal</option>
              <option value="Mensual">Mensual</option>
              <option value="Bimestral">Bimestral</option>
              <option value="Trimestral">Trimestral</option>
              <option value="Semestral">Semestral</option>
              <option value="Anual">Anual</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Fecha de vencimiento (día del mes)</Form.Label>
            <Form.Control
              type="number"
              name="gf_dia_vencimiento"
              value={formData.gf_dia_vencimiento}
              onChange={handleInputChange}
              placeholder="1-31"
              min="1"
              max="31"
            />
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Grupo *</Form.Label>
            <Form.Select
              name="gf_grupo"
              value={formData.gf_grupo}
              onChange={handleInputChange}
              required
            >
              <option value="Personal">Personal</option>
              <option value="Familiar">Familiar</option>
              <option value="Negocio">Negocio</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={12} className="mb-3">
          <Form.Group>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="gf_observaciones"
              value={formData.gf_observaciones}
              onChange={handleInputChange}
              placeholder="Notas adicionales..."
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2 justify-content-end">
        <Button 
          variant="secondary" 
          onClick={() => {
            limpiarFormulario();
            setVistaActual('lista');
          }}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Guardando...
            </>
          ) : (
            modoEdicion ? 'Actualizar' : 'Guardar'
          )}
        </Button>
      </div>
    </Form>
  );

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-money-bill-wave me-2"></i>
            Gastos Fijos / Recurrentes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Alert variant="info" className="mb-3">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Gastos Fijos:</strong> Estos son gastos recurrentes que necesitas todos los meses.
            Crea plantillas para registrarlos rápidamente.
          </Alert>

          {vistaActual === 'lista' ? renderLista() : renderFormulario()}
        </Modal.Body>
      </Modal>

      {/* Modal para crear gasto desde plantilla */}
      <Modal 
        show={showModalCrearGasto} 
        onHide={handleCerrarModalCrearGasto} 
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-plus-circle me-2"></i>
            Crear gasto desde plantilla
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {plantillaSeleccionada && (
            <>
              <Alert variant="info" className="mb-3">
                <div><strong>Plantilla:</strong> {plantillaSeleccionada.gf_nombre}</div>
                <div><strong>Monto sugerido:</strong> ${plantillaSeleccionada.gf_monto.toLocaleString()}</div>
              </Alert>

              <Form onSubmit={handleSubmitCrearGasto}>
                <Form.Group className="mb-3">
                  <Form.Label>Responsable *</Form.Label>
                  <Form.Control
                    type="text"
                    name="responsable"
                    value={formDataGasto.responsable}
                    onChange={handleInputChangeGasto}
                    placeholder="Ingrese el responsable"
                    required
                    autoFocus
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Fecha del gasto</Form.Label>
                  <Form.Control
                    type="date"
                    name="fecha_gasto"
                    value={formDataGasto.fecha_gasto}
                    onChange={handleInputChangeGasto}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Monto (opcional)</Form.Label>
                  <Form.Control
                    type="number"
                    name="monto"
                    value={formDataGasto.monto}
                    onChange={handleInputChangeGasto}
                    placeholder={`Monto sugerido: ${plantillaSeleccionada.gf_monto}`}
                    step="0.01"
                  />
                  <Form.Text className="text-muted">
                    Si no se especifica, se usará el monto de la plantilla
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="observaciones"
                    value={formDataGasto.observaciones}
                    onChange={handleInputChangeGasto}
                    placeholder="Notas adicionales..."
                  />
                </Form.Group>

                <div className="d-flex gap-2 justify-content-end">
                  <Button 
                    variant="secondary" 
                    onClick={handleCerrarModalCrearGasto}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Creando...
                      </>
                    ) : (
                      'Crear gasto'
                    )}
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default GastosFijosModal;
